/**
 * Badge visuel de rôle utilisateur.
 *
 * La hiérarchie, les libellés et les couleurs vivent dans utils/roles :
 * ce fichier ne fait que les afficher.
 */
import { ROLE_BADGE_MAP, getHighestRole, getRoleConfig, normalizeRole } from '../utils/roles';

interface RoleBadgeProps {
  roles: string[];
  showOnlyHighest?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const sizeClasses = {
  sm: 'px-2 py-0.5 text-[10px]',
  md: 'px-3 py-1 text-xs',
  lg: 'px-4 py-1.5 text-sm',
};

export default function RoleBadge({ roles, showOnlyHighest = true, size = 'md' }: RoleBadgeProps) {
  const displayRoles = showOnlyHighest
    ? [getHighestRole(roles)].filter((r): r is string => r !== null)
    : [...new Set(roles.map(normalizeRole))].filter((r) => ROLE_BADGE_MAP[r] !== undefined);

  if (displayRoles.length === 0) {
    return null;
  }

  return (
    <div className="inline-flex flex-wrap items-center gap-1.5">
      {displayRoles.map((role) => {
        const config = getRoleConfig(role);
        if (!config) return null;

        return (
          <span
            key={role}
            className={`inline-flex items-center gap-1.5 font-bold uppercase tracking-wider text-white rounded-full shadow-sm bg-gradient-to-r ${config.from} ${config.to} ${config.darkFrom} ${config.darkTo} transition-transform duration-200 hover:scale-105 cursor-default ${sizeClasses[size]}`}
            title={config.label}
            aria-label={`Role : ${config.label}`}
          >
            <span className="select-none" aria-hidden="true">{config.icon}</span>
            <span>{config.label}</span>
          </span>
        );
      })}
    </div>
  );
}
